/* ─────────────────────────────────────────────────────────
 * MOBILE MENU STORYBOARD
 *
 *    0ms   panel and header divider fade in
 *    0ms   search and bag fade toward the menu button (160ms)
 *    0ms   menu panel drops 12px from the base of the persistent header (260ms)
 *   16ms   first row enters
 *  +16ms   each following row enters, capped after five steps
 *  276ms   all rows have settled
 *
 *    0ms   panel reverses 12px toward the persistent header while content exits
 *   40ms   search and bag begin returning (160ms)
 *  160ms   lines settle 6px lower and fade
 *  200ms   menu icon finishes morphing back to two lines
 *  200ms   panel fade and header controls finish, then the menu is removed
 *
 * MOBILE PRIMARY DISCLOSURES
 *
 *    0ms   submenu layout updates and the first links begin entering
 *  +30ms   each following link begins, capped after four steps
 *  250ms   final staggered link has settled
 *
 * MOBILE MENU DRILL-IN
 *
 *    0ms   outgoing view begins its compact 110ms exit
 *    0ms   incoming view begins fading and sliding 24px
 *   44ms   destination heading and visible rows begin their staggered rise
 *  180ms   destination settles and becomes interactive
 *  ~500ms  final visible destination row has settled
 * ───────────────────────────────────────────────────────── */

const ICON_MORPH = { duration: 200, easing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)' };
const MENU_PANEL_ENTER = { duration: 260, easing: 'cubic-bezier(0.165, 0.84, 0.44, 1)' };
const MENU_PANEL_EXIT = { duration: 200, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' };
const MENU_PANEL_CLOSED = { opacity: 0, y: -12 };
const MENU_LINE_ENTER = { duration: 180, easing: 'cubic-bezier(0.165, 0.84, 0.44, 1)' };
const MENU_LINE_EXIT = { duration: 160, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' };
const MENU_CONTENT_OPEN = { opacity: 1, y: 0 };
const MENU_CONTENT_CLOSED = { opacity: 0, y: 6 };
const MENU_LINE_START = 16;
const MENU_LINE_DELAY = 16;
const MENU_LINE_MAX_STEPS = 5;
const DRILL_VIEW = {
  enterDuration: 180,
  exitDuration: 110,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  distance: 24,
};
const BRAND_DRILL_VIEW = {
  enterDuration: 160,
  exitDuration: 100,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  distance: 24,
};
const BRAND_VIEW_NAMES = new Set(['grinder-brands', 'machine-brands', 'equipment-brands', 'accessory-brands']);
const SUBMENU_ENTER = {
  duration: 160,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  stagger: 30,
  maxStaggeredItems: 4,
};
const SUBMENU_EXIT = {
  duration: 100,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  stagger: 0,
  maxStaggeredItems: 1,
};
const SUBMENU_REFLOW = {
  duration: 150,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
};
const DRILL_CONTENT_REVEAL = {
  duration: 180,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  delay: 44,
  stagger: 32,
  offsetY: 10,
};
const ROASTER_ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
const isSameTabNavigationClick = (event, container) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || !container?.contains(link) || link.hasAttribute('download')) return false;

  return !link.target || link.target.toLowerCase() === '_self';
};
const getDesktopDisclosures = () => Array.from(document.querySelectorAll('[data-desktop-disclosure]'));
const countryFlagFromIsoCode = (countryCode) => {
  const normalizedCode = countryCode?.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalizedCode)) return '';

  return String.fromCodePoint(
    ...Array.from(normalizedCode, (letter) => 127397 + letter.charCodeAt(0)),
  );
};
const initializeCountryFlags = (root = document) => {
  root.querySelectorAll('[data-country-flag]').forEach((flag) => {
    const emoji = countryFlagFromIsoCode(flag.dataset.countryFlag);
    if (!emoji) return;

    flag.textContent = emoji;
    flag.setAttribute('data-country-flag-ready', '');
  });
};
const getDesktopDropdownSwitchDirection = (fromMenu, toMenu) => {
  const orderedMenus = getDesktopDisclosures();
  const fromIndex = orderedMenus.indexOf(fromMenu);
  const toIndex = orderedMenus.indexOf(toMenu);

  return Math.sign(toIndex - fromIndex) || 1;
};
const DESKTOP_DROPDOWN_MOTION = Object.freeze({
  enterDuration: 160,
  switchDuration: 180,
  exitDuration: 110,
  hoverOpenDelay: 75,
  hoverSwitchDelay: 30,
  hoverCloseDelay: 90,
  switchDistance: 8,
  switchOpacity: 0,
  enterEasing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  switchEasing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
  exitEasing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
});
/* ─────────────────────────────────────────────────────────
 * DESKTOP COFFEE VIEW STORYBOARD
 *
 *    0ms   current catalog content yields with a subtle 8px directional drift
 *    0ms   destination content begins the paired crossfade 8px ahead
 *    0ms   shared surface starts resizing from its measured current pixel height
 *  180ms   destination content and shared surface settle together
 * ───────────────────────────────────────────────────────── */
const DESKTOP_COFFEE_VIEW_MOTION = Object.freeze({
  enterDuration: 180,
  resizeDuration: 180,
  distance: 12,
  enterEasing: 'cubic-bezier(0.2, 0, 0, 1)',
});
const DESKTOP_NAV_HOVER_BASE_WIDTH = 100;
const desktopNavHoverControllers = new Map();
const desktopDropdownControllers = new Map();
const desktopDropdownResizeAnimations = new WeakMap();

class DesktopNavHoverController {
  constructor(nav) {
    this.nav = nav;
    this.surface = nav.querySelector('[data-desktop-nav-hover-surface]');
    this.targets = Array.from(nav.querySelectorAll('[data-desktop-nav-hover-target]'));
    this.summaries = Array.from(nav.querySelectorAll(':scope > details > summary'));
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.currentTarget = null;
    this.isVisible = false;
    this.target = { x: 0, width: DESKTOP_NAV_HOVER_BASE_WIDTH };

    if (!this.surface || !this.targets.length) return;

    this.bindEvents();
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.summaries.forEach((summary) => {
      summary.addEventListener(
        'pointerenter',
        () => {
          if (summary.matches('[data-desktop-nav-hover-target]')) {
            this.moveTo(summary);
          } else {
            this.hide();
          }
        },
        { signal },
      );
    });

    this.nav.addEventListener('pointerleave', () => this.syncRestingTarget(), { signal });
    this.nav.addEventListener(
      'header:dropdown-closing',
      () => {
        if (this.nav.matches(':hover') || this.nav.contains(document.activeElement)) return;

        this.hide();
      },
      { signal },
    );
    this.nav.querySelectorAll(':scope > details').forEach((menu) => {
      menu.addEventListener('toggle', () => this.syncRestingTarget(), { signal });
    });
    window.addEventListener(
      'resize',
      () => {
        if (this.currentTarget) this.moveTo(this.currentTarget);
      },
      { signal },
    );
    this.reduceMotion.addEventListener(
      'change',
      () => {
        if (this.currentTarget) this.moveTo(this.currentTarget);
      },
      { signal },
    );
  }

  measure(summary) {
    const navRect = this.nav.getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();

    return {
      x: summaryRect.left - navRect.left,
      width: summaryRect.width,
    };
  }

  moveTo(summary) {
    const shouldAnimate = this.isVisible && !this.reduceMotion.matches;
    this.currentTarget = summary;
    this.target = this.measure(summary);

    if (!shouldAnimate || summary.matches(':focus-visible')) {
      this.surface.setAttribute('data-nav-hover-instant', '');
      this.render();
      this.surface.getBoundingClientRect();
      this.surface.removeAttribute('data-nav-hover-instant');
    } else {
      this.render();
    }

    this.isVisible = true;
    this.surface.style.opacity = '1';
  }

  syncRestingTarget() {
    const hoveredTarget = this.targets.find((target) => target.matches(':hover'));
    if (hoveredTarget) {
      this.moveTo(hoveredTarget);
      return;
    }

    const openTarget = this.targets.find(
      (target) => target.parentElement?.open && !target.parentElement.hasAttribute('data-dropdown-closing'),
    );
    if (openTarget) {
      this.moveTo(openTarget);
      return;
    }

    this.hide();
  }

  hide() {
    this.currentTarget = null;
    this.isVisible = false;
    this.surface.style.opacity = '0';
  }

  render() {
    const scale = this.target.width / DESKTOP_NAV_HOVER_BASE_WIDTH;
    this.surface.style.transform = `translate3d(${this.target.x}px, -50%, 0) scaleX(${scale})`;
  }

  destroy() {
    this.abortController.abort();
  }
}

const initializeDesktopNavHoverSurfaces = (root = document) => {
  root.querySelectorAll('.site-header__desktop-nav').forEach((nav) => {
    if (desktopNavHoverControllers.has(nav)) return;

    desktopNavHoverControllers.set(nav, new DesktopNavHoverController(nav));
  });
};

const initializeDesktopDropdownGroups = (root = document) => {
  root.querySelectorAll('.site-header__desktop-nav').forEach((nav) => {
    nav.querySelectorAll('[data-desktop-dropdown-surface]').forEach((surface) => {
      if (surface.hasAttribute('data-desktop-dropdown-enhanced')) return;

      const groupName = surface.dataset.desktopDropdownSurface;
      const groupedMenus = Array.from(nav.querySelectorAll('[data-desktop-dropdown-group]')).filter(
        (menu) => menu.dataset.desktopDropdownGroup === groupName,
      );

      groupedMenus.forEach((menu) => {
        const key = menu.dataset.desktopDropdownKey;
        const panel = menu.querySelector(':scope > .site-header__desktop-menu-panel');
        if (!key || !panel) return;

        panel.dataset.desktopDropdownContent = key;
        panel.hidden = true;
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
        surface.append(panel);
      });

      surface.setAttribute('data-desktop-dropdown-enhanced', '');
    });
  });
};

class DesktopDropdownController {
  constructor(menu) {
    this.menu = menu;
    this.nav = menu.closest('.site-header__desktop-nav');
    this.groupName = menu.dataset.desktopDropdownGroup || '';
    this.groupKey = menu.dataset.desktopDropdownKey || '';
    this.surface = this.groupName
      ? Array.from(this.nav?.querySelectorAll('[data-desktop-dropdown-surface]') || []).find(
          (surface) => surface.dataset.desktopDropdownSurface === this.groupName,
        )
      : null;
    this.summary = menu.querySelector(':scope > summary');
    this.panel = this.surface
      ? Array.from(this.surface.querySelectorAll('[data-desktop-dropdown-content]')).find(
          (panel) => panel.dataset.desktopDropdownContent === this.groupKey,
        )
      : menu.querySelector(
          ':scope > .site-header__desktop-menu-panel, :scope > .site-header__country-panel, :scope > .site-header__profile-panel',
        );
    const panelScope = this.panel || menu;
    this.coffeeViews = new Map(
      Array.from(panelScope.querySelectorAll('[data-desktop-coffee-view]')).map((view) => [
        view.dataset.desktopCoffeeView,
        view,
      ]),
    );
    this.coffeeViewTriggers = Array.from(
      panelScope.querySelectorAll('[data-desktop-coffee-view-target]'),
    );
    this.coffeeViewBackButtons = Array.from(
      panelScope.querySelectorAll('[data-desktop-coffee-view-back]'),
    );
    this.panelImages = Array.from(panelScope.querySelectorAll('[data-desktop-dropdown-image]'));
    this.panelVideos = Array.from(panelScope.querySelectorAll('[data-desktop-dropdown-video]'));
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1200px)');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.animation = null;
    this.coffeeViewAnimations = [];
    this.coffeeViewTransitionId = 0;
    this.activeCoffeeView = 'main';
    this.openTimer = 0;
    this.closeTimer = 0;

    if (!this.summary || !this.panel) return;

    this.alignPanelToActiveMenu();
    this.setCoffeeViewState('main');
    this.bindEvents();
    this.syncPanelVideos(false);
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.summary.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.warmPanelMedia();
        const instant = event.detail === 0 || this.reduceMotion.matches;

        if (this.menu.open && !this.menu.hasAttribute('data-dropdown-closing')) {
          this.close({ instant });
          return;
        }

        this.open({ instant });
      },
      { signal },
    );

    this.menu.addEventListener(
      'pointerenter',
      (event) => {
        if (
          this.menu.hasAttribute('data-desktop-click-only') ||
          !this.finePointer.matches ||
          event.pointerType === 'touch'
        ) return;

        this.warmPanelMedia();
        this.scheduleOpen();
      },
      { signal },
    );

    this.summary.addEventListener('focus', () => this.warmPanelMedia(), { signal });

    this.panel.addEventListener(
      'click',
      (event) => {
        if (isSameTabNavigationClick(event, this.panel)) this.close({ instant: true });
      },
      { signal },
    );

    this.menu.addEventListener(
      'pointerleave',
      (event) => {
        if (
          this.menu.hasAttribute('data-desktop-click-only') ||
          !this.finePointer.matches ||
          event.pointerType === 'touch'
        ) return;

        this.scheduleClose();
      },
      { signal },
    );

    this.menu.addEventListener(
      'focusout',
      () => {
        queueMicrotask(() => {
          if (
            this.menu.contains(document.activeElement) ||
            this.panel.contains(document.activeElement) ||
            this.menu.matches(':hover') ||
            this.panel.matches(':hover')
          ) {
            return;
          }

          this.close({ instant: true });
        });
      },
      { signal },
    );

    if (this.surface) {
      this.surface.addEventListener(
        'pointerenter',
        (event) => {
          if (
            !this.menu.open ||
            !this.panel.hasAttribute('data-dropdown-content-active') ||
            !this.finePointer.matches ||
            event.pointerType === 'touch'
          ) {
            return;
          }

          window.clearTimeout(this.closeTimer);
        },
        { signal },
      );
      this.surface.addEventListener(
        'pointerleave',
        (event) => {
          if (
            !this.menu.open ||
            !this.panel.hasAttribute('data-dropdown-content-active') ||
            !this.finePointer.matches ||
            event.pointerType === 'touch'
          ) {
            return;
          }

          this.scheduleClose();
        },
        { signal },
      );
      this.surface.addEventListener(
        'focusout',
        () => {
          queueMicrotask(() => {
            if (
              !this.menu.open ||
              !this.panel.hasAttribute('data-dropdown-content-active') ||
              this.menu.contains(document.activeElement) ||
              this.panel.contains(document.activeElement) ||
              this.menu.matches(':hover') ||
              this.panel.matches(':hover')
            ) {
              return;
            }

            this.close({ instant: true });
          });
        },
        { signal },
      );
    }

    this.menu.addEventListener(
      'toggle',
      () => {
        if (this.menu.open) return;

        this.cancelAnimation();
        this.clearMotionState();
        this.resetCoffeeView();
      },
      { signal },
    );

    this.finePointer.addEventListener(
      'change',
      () => {
        this.clearTimers();
        if (!this.finePointer.matches) this.close({ instant: true });
      },
      { signal },
    );

    this.reduceMotion.addEventListener(
      'change',
      () => {
        this.syncPanelVideos();
        if (!this.reduceMotion.matches) return;

        if (this.coffeeViewAnimations.length) {
          const activeView = this.activeCoffeeView;
          this.coffeeViewTransitionId += 1;
          this.cancelCoffeeViewAnimations();
          this.cancelPanelResizeAnimation();
          this.setCoffeeViewState(activeView);
        }
        if (!this.animation) return;

        if (this.menu.hasAttribute('data-dropdown-closing')) {
          this.close({ instant: true });
        } else {
          this.open({ instant: true });
        }
      },
      { signal },
    );

    window.addEventListener('resize', () => this.alignPanelToActiveMenu(), { signal });

    this.coffeeViewTriggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          const keyboardActivation = event.detail === 0;
          if (!keyboardActivation) trigger.blur();

          this.navigateCoffeeView(trigger.dataset.desktopCoffeeViewTarget, {
            instant: keyboardActivation,
            moveFocus: keyboardActivation,
          });
        },
        { signal },
      );
    });

    this.coffeeViewBackButtons.forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          const keyboardActivation = event.detail === 0;
          const currentView = button.closest('[data-desktop-coffee-view]')?.dataset
            .desktopCoffeeView;
          if (!keyboardActivation) button.blur();

          this.navigateCoffeeView('main', {
            instant: keyboardActivation,
            moveFocus: keyboardActivation,
            focusTarget: this.getCoffeeViewTrigger(currentView),
          });
        },
        { signal },
      );
    });
  }

  scheduleOpen() {
    window.clearTimeout(this.closeTimer);
    desktopDropdownControllers.forEach((controller) => {
      if (controller === this) return;

      window.clearTimeout(controller.openTimer);
      controller.openTimer = 0;
    });
    if (this.menu.open && !this.menu.hasAttribute('data-dropdown-closing')) return;

    const anotherMenuIsOpen = Array.from(desktopDropdownControllers.values()).some(
      (controller) => controller !== this && controller.menu.open,
    );
    const delay = anotherMenuIsOpen
      ? DESKTOP_DROPDOWN_MOTION.hoverSwitchDelay
      : DESKTOP_DROPDOWN_MOTION.hoverOpenDelay;

    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => this.open(), delay);
  }

  scheduleClose() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      if (
        this.menu.matches(':hover') ||
        this.panel.matches(':hover') ||
        this.menu.contains(document.activeElement) ||
        this.panel.contains(document.activeElement)
      ) {
        return;
      }

      this.close();
    }, DESKTOP_DROPDOWN_MOTION.hoverCloseDelay);
  }

  showGroupedContent() {
    if (!this.surface) return;

    this.surface.hidden = false;
    this.surface.setAttribute('aria-hidden', 'false');
    this.panel.hidden = false;
    this.panel.inert = false;
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.setAttribute('data-dropdown-content-active', '');
    this.panel.removeAttribute('data-dropdown-content-exiting');
  }

  hideGroupedContent({ hideSurface = false } = {}) {
    if (!this.surface) return;

    this.panel.hidden = true;
    this.panel.inert = true;
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.removeAttribute('data-dropdown-content-active');
    this.panel.removeAttribute('data-dropdown-content-exiting');
    this.panel.removeAttribute('data-dropdown-content-animating');

    if (!hideSurface || this.surface.querySelector('[data-dropdown-content-active]')) return;

    this.surface.hidden = true;
    this.surface.setAttribute('aria-hidden', 'true');
    this.surface.removeAttribute('data-dropdown-animating');
  }

  open({ instant = false } = {}) {
    this.clearTimers();
    this.alignPanelToActiveMenu({ instant: instant || this.reduceMotion.matches });
    this.warmPanelMedia();

    const otherOpenControllers = Array.from(desktopDropdownControllers.values()).filter(
      (controller) => controller !== this && controller.menu.open,
    );
    const switchingController =
      otherOpenControllers.find(
        (controller) =>
          this.surface &&
          controller.surface === this.surface &&
          !controller.menu.hasAttribute('data-dropdown-closing'),
      ) ||
      otherOpenControllers.find(
        (controller) => !controller.menu.hasAttribute('data-dropdown-closing'),
      ) || otherOpenControllers[0];
    const isSharedSurfaceSwitch = Boolean(
      this.surface && switchingController?.surface === this.surface,
    );
    const switchStartHeight = isSharedSurfaceSwitch
      ? switchingController.readResizablePanelHeight()
      : null;
    const isSwitching = Boolean(switchingController);
    const switchDirection = switchingController
      ? getDesktopDropdownSwitchDirection(switchingController.menu, this.menu)
      : 1;

    const sharedSurfaceWasClosing = isSharedSurfaceSwitch && switchingController.menu.hasAttribute('data-dropdown-closing');
    const motionTarget = this.surface && (!isSharedSurfaceSwitch || sharedSurfaceWasClosing) ? this.surface : this.panel;
    const currentStyle = this.readPanelStyle(motionTarget);
    const wasOpen = this.menu.open;
    const wasClosing = this.menu.hasAttribute('data-dropdown-closing') || sharedSurfaceWasClosing;
    this.cancelAnimation();
    this.clearMotionState();
    this.showGroupedContent();
    this.menu.open = true;
    this.syncPanelVideos();
    this.resizePanel(switchStartHeight, {
      instant: instant || !isSharedSurfaceSwitch,
      duration: DESKTOP_DROPDOWN_MOTION.switchDuration,
      easing: DESKTOP_DROPDOWN_MOTION.switchEasing,
    });

    desktopDropdownControllers.forEach((controller) => {
      if (controller === this) return;

      const sharesSurface = Boolean(this.surface && controller.surface === this.surface);
      const isSwitchingController = controller === switchingController;

      controller.close({
        instant: controller !== switchingController || instant,
        switchDirection: isSwitchingController ? switchDirection : 0,
        preserveSurface: sharesSurface,
      });
    });

    if (instant || this.reduceMotion.matches || !this.panel.animate) return;
    if (wasOpen && !wasClosing) return;

    this.menu.setAttribute('data-dropdown-animating', 'open');
    if (motionTarget === this.surface) {
      this.surface.setAttribute('data-dropdown-animating', 'open');
    } else if (this.surface) {
      this.panel.setAttribute('data-dropdown-content-animating', 'open');
    }
    let startOpacity = 0;
    let startTransform = 'translate3d(0, -8px, 0) scale(0.985)';

    if (isSwitching) {
      startOpacity = DESKTOP_DROPDOWN_MOTION.switchOpacity;
      startTransform = `translate3d(${switchDirection * DESKTOP_DROPDOWN_MOTION.switchDistance}px, 0, 0)`;
    }

    if (wasClosing) {
      startOpacity = currentStyle.opacity;
      startTransform = currentStyle.transform;
    }

    const animation = motionTarget.animate(
      [
        { opacity: startOpacity, transform: startTransform },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      ],
      {
        duration: isSwitching
          ? DESKTOP_DROPDOWN_MOTION.switchDuration
          : DESKTOP_DROPDOWN_MOTION.enterDuration,
        easing: isSwitching
          ? DESKTOP_DROPDOWN_MOTION.switchEasing
          : DESKTOP_DROPDOWN_MOTION.enterEasing,
        fill: 'both',
      },
    );

    this.trackAnimation(animation, () => this.clearMotionState());
  }

  close({ instant = false, switchDirection = 0, preserveSurface = false } = {}) {
    this.clearTimers();
    if (!this.menu.open) return;
    this.syncPanelVideos(false);

    const isSwitching = switchDirection !== 0;
    const motionTarget = this.surface && !preserveSurface ? this.surface : this.panel;
    const currentStyle = this.readPanelStyle(motionTarget);
    this.cancelAnimation();

    if (this.surface) {
      this.panel.inert = true;
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.removeAttribute('data-dropdown-content-active');
      if (isSwitching) this.panel.setAttribute('data-dropdown-content-exiting', '');
    }

    if (instant || this.reduceMotion.matches || !motionTarget.animate) {
      this.menu.open = false;
      this.clearMotionState();
      this.hideGroupedContent({ hideSurface: Boolean(this.surface && !preserveSurface) });
      this.resetCoffeeView();
      return;
    }

    this.menu.setAttribute('data-dropdown-animating', 'close');
    this.menu.setAttribute('data-dropdown-closing', '');
    if (motionTarget === this.surface) {
      this.surface.setAttribute('data-dropdown-animating', 'close');
    } else if (this.surface) {
      this.panel.setAttribute('data-dropdown-content-animating', 'close');
    }
    this.menu.dispatchEvent(new CustomEvent('header:dropdown-closing', { bubbles: true }));
    const endTransform = isSwitching
      ? `translate3d(${-switchDirection * DESKTOP_DROPDOWN_MOTION.switchDistance}px, 0, 0)`
      : 'translate3d(0, -6px, 0) scale(0.992)';
    const animation = motionTarget.animate(
      [
        {
          opacity: currentStyle.opacity,
          transform: currentStyle.transform,
        },
        { opacity: 0, transform: endTransform },
      ],
      {
        duration: isSwitching
          ? DESKTOP_DROPDOWN_MOTION.switchDuration
          : DESKTOP_DROPDOWN_MOTION.exitDuration,
        easing: isSwitching
          ? DESKTOP_DROPDOWN_MOTION.switchEasing
          : DESKTOP_DROPDOWN_MOTION.exitEasing,
        fill: 'both',
      },
    );

    this.trackAnimation(animation, () => {
      this.menu.open = false;
      this.clearMotionState();
      const anotherGroupedMenuIsOpen = Array.from(desktopDropdownControllers.values()).some(
        (controller) =>
          controller !== this && controller.surface === this.surface && controller.menu.open,
      );
      this.hideGroupedContent({
        hideSurface: Boolean(this.surface && !preserveSurface && !anotherGroupedMenuIsOpen),
      });
      this.resetCoffeeView();
    });
  }

  readPanelStyle(target = this.panel) {
    const style = getComputedStyle(target);

    return {
      opacity: Number.parseFloat(style.opacity) || 0,
      transform: style.transform === 'none' ? 'translate3d(0, 0, 0) scale(1)' : style.transform,
    };
  }

  alignPanelToActiveMenu({ instant = false } = {}) {
    const groupName = this.menu.dataset.desktopDropdownGroup;
    if (!groupName || !this.nav || !this.surface) return;

    const offset = this.menu.getBoundingClientRect().left - this.nav.getBoundingClientRect().left;
    if (instant) this.surface.setAttribute('data-dropdown-instant', '');
    this.surface.style.setProperty('--desktop-dropdown-shared-offset', `${offset}px`);
    if (instant) {
      this.surface.getBoundingClientRect();
      this.surface.removeAttribute('data-dropdown-instant');
    }
  }

  trackAnimation(animation, onFinish) {
    this.animation = animation;
    animation.finished
      .then(() => {
        if (this.animation !== animation) return;

        this.animation = null;
        onFinish();
        animation.cancel();
      })
      .catch(() => {});
  }

  cancelAnimation() {
    this.animation?.cancel();
    this.animation = null;
  }

  clearMotionState() {
    this.menu.removeAttribute('data-dropdown-animating');
    this.menu.removeAttribute('data-dropdown-closing');
    this.surface?.removeAttribute('data-dropdown-animating');
    this.panel.removeAttribute('data-dropdown-content-animating');
    this.panel.removeAttribute('data-dropdown-content-exiting');
  }

  getCoffeeViewTrigger(viewName) {
    return this.coffeeViewTriggers.find(
      (trigger) => trigger.dataset.desktopCoffeeViewTarget === viewName,
    );
  }

  setCoffeeViewState(viewName) {
    if (!this.coffeeViews.size) return;

    const nextView = this.coffeeViews.has(viewName) ? viewName : 'main';
    this.activeCoffeeView = nextView;
    this.panel.dataset.activeView = nextView;
    this.coffeeViews.forEach((view, name) => {
      const isActive = name === nextView;
      view.toggleAttribute('inert', !isActive);
      view.setAttribute('aria-hidden', String(!isActive));
    });
    this.coffeeViewTriggers.forEach((trigger) => {
      trigger.setAttribute(
        'aria-expanded',
        String(trigger.dataset.desktopCoffeeViewTarget === nextView),
      );
    });
  }

  cancelCoffeeViewAnimations() {
    this.coffeeViewAnimations.forEach((animation) => animation?.cancel());
    this.coffeeViewAnimations = [];
    this.coffeeViews.forEach((view) => {
      view.removeAttribute('data-view-entering');
      view.removeAttribute('data-view-exiting');
    });
  }

  readResizablePanelHeight() {
    const resizeTarget = this.surface || this.panel;

    return Number.parseFloat(getComputedStyle(resizeTarget).height) || null;
  }

  readResizablePanelTargetHeight() {
    return (
      Number.parseFloat(
        getComputedStyle(this.panel).getPropertyValue('--desktop-dropdown-panel-height'),
      ) || null
    );
  }

  cancelPanelResizeAnimation() {
    const resizeTarget = this.surface || this.panel;
    const animation = desktopDropdownResizeAnimations.get(resizeTarget);
    if (!animation) return;

    animation.cancel();
    desktopDropdownResizeAnimations.delete(resizeTarget);
  }

  resizePanel(startHeight, {
    instant = false,
    duration = DESKTOP_COFFEE_VIEW_MOTION.resizeDuration,
    easing = DESKTOP_COFFEE_VIEW_MOTION.enterEasing,
  } = {}) {
    const resizeTarget = this.surface || this.panel;
    const targetHeight = this.readResizablePanelTargetHeight();
    if (!targetHeight) return;

    this.cancelPanelResizeAnimation();
    resizeTarget.style.height = `${targetHeight}px`;
    if (
      instant ||
      this.reduceMotion.matches ||
      !resizeTarget.animate ||
      !startHeight ||
      Math.abs(startHeight - targetHeight) < 0.5
    ) {
      return;
    }

    const animation = resizeTarget.animate(
      [{ height: `${startHeight}px` }, { height: `${targetHeight}px` }],
      {
        duration,
        easing,
        fill: 'both',
      },
    );
    desktopDropdownResizeAnimations.set(resizeTarget, animation);
    animation.finished
      .then(() => {
        if (desktopDropdownResizeAnimations.get(resizeTarget) !== animation) return;

        desktopDropdownResizeAnimations.delete(resizeTarget);
        animation.cancel();
      })
      .catch(() => {});
  }

  moveCoffeeViewFocus(target) {
    requestAnimationFrame(() => target?.focus({ preventScroll: true }));
  }

  navigateCoffeeView(
    nextView,
    { instant = false, moveFocus = false, focusTarget = null } = {},
  ) {
    if (!this.coffeeViews.has(nextView) || nextView === this.activeCoffeeView) return;

    const outgoing = this.coffeeViews.get(this.activeCoffeeView);
    const incoming = this.coffeeViews.get(nextView);
    const previousView = this.activeCoffeeView;
    const currentTransition = ++this.coffeeViewTransitionId;
    const resolvedFocusTarget =
      focusTarget || incoming.querySelector('[data-desktop-coffee-view-back]');
    const startHeight = this.readResizablePanelHeight();

    this.cancelCoffeeViewAnimations();
    this.cancelPanelResizeAnimation();
    this.setCoffeeViewState(previousView);

    if (instant || this.reduceMotion.matches || !incoming.animate || !outgoing.animate) {
      this.setCoffeeViewState(nextView);
      this.resizePanel(startHeight, { instant: true });
      if (moveFocus) this.moveCoffeeViewFocus(resolvedFocusTarget);
      return;
    }

    const isBackNavigation = nextView === 'main';
    const direction = isBackNavigation ? -1 : 1;
    this.activeCoffeeView = nextView;
    this.panel.dataset.activeView = nextView;
    this.resizePanel(startHeight);
    this.coffeeViewTriggers.forEach((trigger) => {
      trigger.setAttribute(
        'aria-expanded',
        String(trigger.dataset.desktopCoffeeViewTarget === nextView),
      );
    });
    outgoing.toggleAttribute('inert', true);
    outgoing.setAttribute('aria-hidden', 'true');
    incoming.toggleAttribute('inert', false);
    incoming.setAttribute('aria-hidden', 'false');
    incoming.setAttribute('data-view-entering', '');

    const incomingAnimation = incoming.animate(
      [
        {
          opacity: 0.88,
          transform: `translate3d(${direction * DESKTOP_COFFEE_VIEW_MOTION.distance}px, 0, 0)`,
        },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
      ],
      {
        duration: DESKTOP_COFFEE_VIEW_MOTION.enterDuration,
        easing: DESKTOP_COFFEE_VIEW_MOTION.enterEasing,
        fill: 'both',
      },
    );
    const animations = [incomingAnimation];
    this.coffeeViewAnimations = animations;

    Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(() => {
      if (currentTransition !== this.coffeeViewTransitionId) return;

      this.setCoffeeViewState(nextView);
      this.cancelCoffeeViewAnimations();
      if (moveFocus) this.moveCoffeeViewFocus(resolvedFocusTarget);
    });
  }

  resetCoffeeView() {
    if (!this.coffeeViews.size) return;

    this.coffeeViewTransitionId += 1;
    this.cancelCoffeeViewAnimations();
    this.setCoffeeViewState('main');
  }

  handleEscape() {
    if (this.activeCoffeeView === 'main') return false;

    const currentView = this.activeCoffeeView;
    this.navigateCoffeeView('main', {
      instant: true,
      moveFocus: true,
      focusTarget: this.getCoffeeViewTrigger(currentView),
    });
    return true;
  }

  clearTimers() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.openTimer = 0;
    this.closeTimer = 0;
  }

  warmPanelMedia() {
    this.panelImages.forEach((image) => {
      if (image.complete) return;

      image.loading = 'eager';
      image.decode?.().catch(() => {});
    });

    this.panelVideos.forEach((video) => {
      if (video.dataset.preloadReady === 'true') return;

      video.dataset.preloadReady = 'true';
      video.preload = 'auto';
      video.load();
    });
  }

  syncPanelVideos(shouldPlay = this.menu.open && !this.reduceMotion.matches) {
    if (shouldPlay) this.warmPanelMedia();

    this.panelVideos.forEach((video) => {
      if (!shouldPlay) {
        video.pause();
        return;
      }

      video.play().catch(() => {});
    });
  }

  destroy() {
    this.syncPanelVideos(false);
    this.abortController.abort();
    this.clearTimers();
    this.cancelAnimation();
    this.cancelCoffeeViewAnimations();
    this.cancelPanelResizeAnimation();
  }
}

const initializeDesktopDropdowns = (root = document) => {
  root.querySelectorAll('[data-desktop-disclosure]').forEach((menu) => {
    if (desktopDropdownControllers.has(menu)) return;

    desktopDropdownControllers.set(menu, new DesktopDropdownController(menu));
  });
};

initializeCountryFlags();
initializeDesktopDropdownGroups();
initializeDesktopDropdowns();
initializeDesktopNavHoverSurfaces();
document.addEventListener('shopify:section:load', (event) => {
  initializeCountryFlags(event.target);
  initializeDesktopDropdownGroups(event.target);
  initializeDesktopDropdowns(event.target);
});
document.addEventListener('shopify:section:load', (event) => initializeDesktopNavHoverSurfaces(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  desktopDropdownControllers.forEach((controller, menu) => {
    if (!event.target.contains(menu)) return;

    controller.destroy();
    desktopDropdownControllers.delete(menu);
  });

  desktopNavHoverControllers.forEach((controller, nav) => {
    if (!event.target.contains(nav)) return;

    controller.destroy();
    desktopNavHoverControllers.delete(nav);
  });
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-desktop-disclosure], [data-desktop-dropdown-surface]')) return;
  desktopDropdownControllers.forEach((controller) => controller.close());
});

document.addEventListener('click', (event) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const link = event.target instanceof Element
    ? event.target.closest('[data-profile-favourites-link]')
    : null;
  if (!link || (link.target && link.target.toLowerCase() !== '_self')) return;

  const destination = new URL(link.href, window.location.href);
  if (
    destination.origin !== window.location.origin ||
    destination.pathname !== window.location.pathname ||
    destination.search !== window.location.search ||
    destination.hash !== '#favourites'
  ) {
    return;
  }

  const favouritesView = document.querySelector('[data-for-you-view="favourites"]');
  if (!favouritesView) return;

  requestAnimationFrame(() => favouritesView.click());
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openMenu =
    getDesktopDisclosures().find(
      (menu) => menu.open && !menu.hasAttribute('data-dropdown-closing'),
    ) || getDesktopDisclosures().find((menu) => menu.open);
  if (!openMenu) return;

  event.preventDefault();
  const controller = desktopDropdownControllers.get(openMenu);
  if (controller?.handleEscape()) return;

  controller?.close({ instant: true });
  openMenu.querySelector(':scope > summary')?.focus({ preventScroll: true });
});

const clamp = (value) => Math.min(1, Math.max(0, value));

const playTween = (element, timing, render, delay = 0) => {
  if (!element?.animate) return null;

  return element.animate([render(0), render(1)], {
    duration: timing.duration,
    delay,
    easing: timing.easing,
    fill: 'both',
  });
};

const readOpacity = (element) => Number.parseFloat(getComputedStyle(element).opacity) || 0;

const readTransform = (element) => {
  const transform = getComputedStyle(element).transform;
  const matrix = new DOMMatrixReadOnly(transform === 'none' ? undefined : transform);

  return {
    x: matrix.m41,
    y: matrix.m42,
    rotation: Math.atan2(matrix.m12, matrix.m11) * (180 / Math.PI),
    scaleX: Math.hypot(matrix.m11, matrix.m12),
  };
};

const interpolate = (from, to, progress) => from + (to - from) * progress;

document.querySelectorAll('[data-header-menu]').forEach((menu) => {
  const trigger = menu.querySelector(':scope > summary');
  const panel = menu.querySelector('[data-header-menu-panel]');
  const menuRevealLines = Array.from(menu.querySelectorAll('[data-menu-reveal-line]'));
  const disclosureItems = Array.from(menu.querySelectorAll('.mobile-menu__item'));
  const disclosureSummaries = disclosureItems
    .map((item) => item.querySelector(':scope > summary'))
    .filter(Boolean);
  const viewStage = menu.querySelector('[data-mobile-menu-view-stage]');
  const mobileViews = new Map(
    Array.from(menu.querySelectorAll('[data-mobile-menu-view]')).map((view) => [view.dataset.mobileMenuView, view]),
  );
  const roasterView = mobileViews.get('roasters');
  const viewOpenButtons = Array.from(menu.querySelectorAll('[data-menu-view-target]'));
  const viewBackButtons = Array.from(menu.querySelectorAll('[data-menu-view-back]'));
  const roasterList = menu.querySelector('[data-roaster-list]');
  let roasterLinks = Array.from(menu.querySelectorAll('[data-roaster-name]'));
  let roasterSections = Array.from(menu.querySelectorAll('[data-roaster-section]'));
  const roasterIndex = menu.querySelector('[data-roaster-index]');
  let roasterIndexLetters = Array.from(menu.querySelectorAll('[data-roaster-index-letter]'));
  const roasterEmpty = menu.querySelector('[data-roaster-empty]');
  const topLine = trigger?.querySelector('.menu-icon__line--top');
  const bottomLine = trigger?.querySelector('.menu-icon__line--bottom');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 1199px)');
  let targetOpen = menu.open;
  let transitionId = 0;
  let activeAnimations = [];
  let viewTransitionId = 0;
  let activeViewAnimations = [];
  const disclosureReflowAnimations = new Map();
  let activeView = 'main';
  let targetView = 'main';
  const disclosureStates = new Map(
    disclosureItems.map((item) => [
      item,
      {
        targetOpen: item.open,
        transitionId: 0,
        animations: [],
      },
    ]),
  );
  let activeDrillListAnimations = [];
  let activeRoasterIndex = 0;
  let roasterIndexPointerId = null;
  let menuInertElements = [];

  const usesDocumentScroll = () => menu.dataset.modalDocumentScroll === 'active';

  const resetPanelScroll = () => {
    if (usesDocumentScroll()) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    panel.scrollTop = 0;
  };

  const resetRoasterListScroll = () => {
    if (roasterList && !usesDocumentScroll()) roasterList.scrollTop = 0;
  };

  const getDocumentViewport = () => {
    const top = window.visualViewport?.offsetTop || 0;
    return {
      top,
      bottom: top + (window.visualViewport?.height || window.innerHeight),
    };
  };

  const getDocumentOffsetTop = (element) =>
    window.scrollY + element.getBoundingClientRect().top;

  panel.toggleAttribute('inert', !menu.open);
  viewStage?.setAttribute('data-active-view', 'main');

  const cancelAnimations = () => {
    activeAnimations.forEach((animation) => animation?.cancel());
    activeAnimations = [];
  };

  const cancelViewAnimations = () => {
    activeViewAnimations.forEach((animation) => animation?.cancel());
    activeViewAnimations = [];
  };

  const cancelDisclosureAnimations = (item) => {
    const state = disclosureStates.get(item);
    if (!state) return;

    const animations = new Set(state.animations);
    disclosureReflowAnimations.forEach((entry, target) => {
      if (animations.has(entry.animation)) disclosureReflowAnimations.delete(target);
    });
    state.animations.forEach((animation) => animation?.cancel());
    state.animations = [];
  };

  const cancelDisclosureReflows = () => {
    disclosureReflowAnimations.forEach(({ animation, owner }) => {
      animation.cancel();
      const state = disclosureStates.get(owner);
      if (state) state.animations = state.animations.filter((candidate) => candidate !== animation);
    });
    disclosureReflowAnimations.clear();
  };

  const getDisclosureReflowTargets = (item) => {
    const primary = item.parentElement;
    const mainView = primary?.parentElement;
    if (!primary || !mainView) return [];

    const itemIndex = Array.from(primary.children).indexOf(item);
    const primaryIndex = Array.from(mainView.children).indexOf(primary);

    return [
      ...Array.from(primary.children).slice(itemIndex + 1),
      ...Array.from(mainView.children).slice(primaryIndex + 1),
    ];
  };

  const animateDisclosureReflow = (owner, targets, previousTops) => {
    targets.forEach((target) => {
      const existing = disclosureReflowAnimations.get(target);
      if (!existing) return;

      existing.animation.cancel();
      const state = disclosureStates.get(existing.owner);
      if (state) {
        state.animations = state.animations.filter(
          (candidate) => candidate !== existing.animation,
        );
      }
      disclosureReflowAnimations.delete(target);
    });

    const targetTops = new Map(targets.map((target) => [target, target.getBoundingClientRect().top]));

    return targets
      .map((target) => {
        const offset = previousTops.get(target) - targetTops.get(target);
        if (!Number.isFinite(offset) || Math.abs(offset) < 0.5 || !target.animate) return null;

        const animation = target.animate(
          [
            { transform: `translate3d(0, ${offset}px, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          { ...SUBMENU_REFLOW, fill: 'both' },
        );

        disclosureReflowAnimations.set(target, { animation, owner });
        return animation;
      })
      .filter(Boolean);
  };

  const cancelDrillEntryAnimations = () => {
    activeDrillListAnimations.forEach((animation) => animation?.cancel());
    activeDrillListAnimations = [];
  };

  const setMenuBackgroundInert = (inert) => {
    if (!inert) {
      menuInertElements.forEach((element) => element.removeAttribute('inert'));
      menuInertElements = [];
      return;
    }

    if (menuInertElements.length) return;

    let current = menu;
    while (current.parentElement) {
      Array.from(current.parentElement.children).forEach((sibling) => {
        if (sibling === current || sibling.hasAttribute('inert')) return;

        sibling.setAttribute('inert', '');
        menuInertElements.push(sibling);
      });

      current = current.parentElement;
      if (current === document.body) break;
    }
  };

  const refreshRoasterElements = () => {
    roasterLinks = Array.from(menu.querySelectorAll('[data-roaster-name]'));
    roasterSections = Array.from(menu.querySelectorAll('[data-roaster-section]'));
    roasterIndexLetters = Array.from(menu.querySelectorAll('[data-roaster-index-letter]'));
  };

  const renderRoasterVendors = (vendors) => {
    if (!roasterList || !roasterIndex || !roasterEmpty) return;

    const vendorData = new Map();
    const withDefaultAvailability = (destination) => {
      const url = new URL(destination, window.location.origin);

      if (!url.searchParams.has('filter.v.availability')) {
        url.searchParams.set('filter.v.availability', '1');
      }

      return `${url.pathname}${url.search}${url.hash}`;
    };

    vendors.forEach((vendor) => {
      const name = (typeof vendor === 'string' ? vendor : vendor?.name)?.trim();
      if (!name) return;

      const fallbackUrl = `${roasterList.dataset.roasterLinkUrl}${encodeURIComponent(name)}`;
      const destination = typeof vendor === 'object' && vendor.url ? vendor.url : fallbackUrl;
      const identity = name.toLocaleLowerCase();
      const existing = vendorData.get(identity);

      vendorData.set(identity, {
        name: existing?.name || name,
        destination: withDefaultAvailability(destination),
        comingSoon: existing?.comingSoon === true || (typeof vendor === 'object' && vendor.comingSoon === true),
        isNew: existing?.isNew === true || (typeof vendor === 'object' && vendor.isNew === true),
      });
    });

    const uniqueVendors = [...vendorData.values()].sort((first, second) =>
      first.name.localeCompare(second.name, undefined, { sensitivity: 'base' }),
    );
    const vendorGroups = new Map();

    uniqueVendors.forEach((vendor) => {
      const letter = [...vendor.name][0]?.toLocaleUpperCase() || '#';
      const group = vendorGroups.get(letter) || [];
      group.push(vendor);
      vendorGroups.set(letter, group);
    });

    roasterList.querySelectorAll('[data-roaster-section]').forEach((section) => section.remove());
    roasterIndex.replaceChildren();

    ROASTER_ALPHABET.forEach((letter) => {
      const indexLetter = document.createElement('span');
      indexLetter.className = 'mobile-menu-roasters__index-letter';
      indexLetter.dataset.roasterIndexLetter = letter;
      indexLetter.toggleAttribute('data-roaster-index-available', vendorGroups.has(letter));
      indexLetter.textContent = letter;
      roasterIndex.append(indexLetter);
    });

    [...vendorGroups.entries()].forEach(([letter, groupVendors], groupIndex) => {
      const section = document.createElement('section');
      const heading = document.createElement('h3');
      const links = document.createElement('div');
      const headingId = `${roasterList.id || 'MobileRoasterList'}-Section-${groupIndex}`;

      section.className = 'mobile-menu-roasters__section';
      section.dataset.roasterSection = '';
      section.dataset.roasterLetter = letter;
      section.setAttribute('aria-labelledby', headingId);
      heading.className = 'mobile-menu-roasters__section-title';
      heading.id = headingId;
      heading.textContent = letter;
      links.className = 'mobile-menu-roasters__section-links';

      groupVendors.forEach((roaster) => {
        const link = document.createElement('a');
        const name = document.createElement('span');

        if (roaster.comingSoon) {
          link.setAttribute('role', 'link');
          link.setAttribute('aria-disabled', 'true');
        } else {
          link.href = roaster.destination;
          link.dataset.pageLoadingPriority = '';
        }
        link.dataset.roasterReveal = '';
        link.dataset.roasterName = roaster.name.toLocaleLowerCase();
        name.className = 'roaster-menu__name';
        name.textContent = roaster.name;
        link.append(name);

        if (roaster.comingSoon || roaster.isNew) {
          const chip = document.createElement('span');
          const chipLabel = document.createElement('span');

          chip.className = 'roaster-menu__new-chip';
          chipLabel.className = 'roaster-menu__new-chip-label';
          if (roaster.comingSoon) chip.classList.add('roaster-coming-soon-chip');
          chipLabel.textContent = roaster.comingSoon ? 'Coming soon' : 'New';
          chip.append(chipLabel);
          link.append(chip);
        }

        links.append(link);
      });

      section.append(heading, links);
      roasterList.insertBefore(section, roasterEmpty);
    });

    refreshRoasterElements();
    roasterEmpty.toggleAttribute('hidden', roasterLinks.length > 0);

    if (targetView === 'roasters') {
      animateDrillContent(roasterView, { delay: activeView === 'roasters' ? 0 : DRILL_CONTENT_REVEAL.delay });
    }

    window.requestAnimationFrame(updateRoasterListScrollState);
  };

  const loadRoasterVendors = async () => {
    if (!roasterList?.dataset.roasterSourceUrl) return;

    roasterList.setAttribute('aria-busy', 'true');

    try {
      const { loadRoasterDirectory } = await import('./roaster-directory-data.js');
      const vendors = await loadRoasterDirectory(roasterList.dataset.roasterSourceUrl);
      renderRoasterVendors(vendors);
      resetRoasterIndex();
    } catch (error) {
      console.error('Unable to load coffee roasters.', error);
      roasterEmpty?.toggleAttribute('hidden', roasterLinks.length > 0);
    } finally {
      roasterList.removeAttribute('aria-busy');
    }
  };

  const finishDisclosureInstantly = (item, opening) => {
    const state = disclosureStates.get(item);
    if (!state) return;

    state.transitionId += 1;
    state.targetOpen = opening;
    cancelDisclosureReflows();
    cancelDisclosureAnimations(item);
    item.dataset.iconInstant = 'true';
    item.dataset.submenuInstant = 'true';
    item.open = opening;
    delete item.dataset.submenuAnimating;
    delete item.dataset.submenuInitial;
    delete item.dataset.submenuTarget;

    requestAnimationFrame(() => {
      delete item.dataset.iconInstant;
      delete item.dataset.submenuInstant;
    });
  };

  const getDisclosureLinks = (item) => {
    const submenu = item.querySelector(':scope > .mobile-menu__submenu-wrap .mobile-menu__submenu');
    if (!submenu) return [];

    return Array.from(submenu.children)
      .map((child) =>
        child.matches('[data-menu-tree-branch]')
          ? child.querySelector(':scope > [data-menu-view-target]')
          : child,
      )
      .filter((child) => child?.matches('.mobile-menu__submenu-link, [data-menu-disclosure-reveal]'));
  };

  const animateDisclosure = async (item, opening, { instant = false } = {}) => {
    const state = disclosureStates.get(item);
    if (!state || opening === state.targetOpen) return;

    if (opening) {
      disclosureItems.forEach((otherItem) => {
        if (otherItem !== item && disclosureStates.get(otherItem)?.targetOpen) {
          void animateDisclosure(otherItem, false);
        }
      });
    }

    state.targetOpen = opening;
    const currentTransition = ++state.transitionId;
    const links = getDisclosureLinks(item);

    if (instant || reduceMotion.matches || !links[0]?.animate) {
      finishDisclosureInstantly(item, opening);
      return;
    }

    const potentialOpeningReflowTargets = opening ? getDisclosureReflowTargets(item) : [];
    const openingReflowTargets = potentialOpeningReflowTargets.some((target) =>
      disclosureReflowAnimations.has(target),
    )
      ? potentialOpeningReflowTargets
      : [];
    item.dataset.submenuAnimating = opening ? 'open' : 'close';

    if (opening && !item.open) {
      item.dataset.submenuInitial = 'true';
      item.dataset.submenuTarget = 'closed';
      item.open = true;
      await new Promise(requestAnimationFrame);

      if (currentTransition !== state.transitionId) return;
    }

    const openingPreviousTops = new Map(
      openingReflowTargets.map((target) => [target, target.getBoundingClientRect().top]),
    );

    const starts = links.map((link) => {
      const transform = readTransform(link);

      return {
        opacity: readOpacity(link),
        y: transform.y,
      };
    });

    cancelDisclosureAnimations(item);
    if (opening) item.dataset.submenuTarget = 'open';

    const target = opening ? { opacity: 1, y: 0 } : { opacity: 0, y: -2 };
    const timing = opening ? SUBMENU_ENTER : SUBMENU_EXIT;

    const linkAnimations = links
      .map((link, index) => {
        const start = starts[index];
        const staggerIndex = opening ? index : links.length - index - 1;
        const delay =
          (timing.delay || 0) +
          Math.min(staggerIndex, timing.maxStaggeredItems - 1) * timing.stagger;

        return playTween(
          link,
          timing,
          (progress) => ({
            opacity: clamp(interpolate(start.opacity, target.opacity, progress)),
            transform:
              opening && progress === 1
                ? 'none'
                : `translate3d(0, ${interpolate(start.y, target.y, progress)}px, 0)`,
          }),
          delay,
        );
      })
      .filter(Boolean);
    const openingReflowAnimations = opening
      ? animateDisclosureReflow(item, openingReflowTargets, openingPreviousTops)
      : [];
    state.animations = [...linkAnimations, ...openingReflowAnimations];

    delete item.dataset.submenuInitial;

    await Promise.all(state.animations.map((animation) => animation.finished.catch(() => {})));

    if (currentTransition !== state.transitionId) return;

    if (!opening) {
      const reflowTargets = getDisclosureReflowTargets(item);
      const previousTops = new Map(reflowTargets.map((target) => [target, target.getBoundingClientRect().top]));

      item.dataset.submenuTarget = 'closed';
      item.open = false;
      state.animations = animateDisclosureReflow(item, reflowTargets, previousTops);

      await Promise.all(state.animations.map((animation) => animation.finished.catch(() => {})));

      if (currentTransition !== state.transitionId) return;
    }

    delete item.dataset.submenuAnimating;
    delete item.dataset.submenuTarget;
    cancelDisclosureAnimations(item);
  };

  const setActiveRoasterIndex = (index, { scroll = false } = {}) => {
    if (!roasterIndexLetters.length) return;

    const nextIndex = Math.min(roasterIndexLetters.length - 1, Math.max(0, index));
    const letter = ROASTER_ALPHABET[nextIndex];
    activeRoasterIndex = nextIndex;

    roasterIndexLetters.forEach((indexLetter, letterIndex) => {
      indexLetter.toggleAttribute('aria-current', letterIndex === nextIndex);
    });
    roasterIndex?.setAttribute('aria-valuenow', String(nextIndex));
    roasterIndex?.setAttribute('aria-valuetext', letter);

    if (scroll && roasterList && roasterSections.length) {
      const targetSection =
        roasterSections.find((section) => ROASTER_ALPHABET.indexOf(section.dataset.roasterLetter) >= nextIndex) ||
        roasterSections.at(-1);
      if (usesDocumentScroll()) {
        const viewport = getDocumentViewport();
        window.scrollTo({
          top: Math.max(
            0,
            getDocumentOffsetTop(targetSection) - viewport.top - getRoasterListFadeHeight(),
          ),
          behavior: 'auto',
        });
      } else {
        roasterList.scrollTop = Math.max(
          0,
          targetSection.offsetTop - getRoasterListFadeHeight(),
        );
      }
      updateRoasterListScrollState();
    }
  };

  const resetRoasterIndex = () => {
    roasterSections.forEach((section) => section.removeAttribute('hidden'));
    roasterIndex?.toggleAttribute('hidden', roasterSections.length === 0);
    roasterIndex?.removeAttribute('data-scrubbing');
    roasterIndex?.setAttribute('aria-valuemax', String(ROASTER_ALPHABET.length - 1));
    roasterIndexPointerId = null;
    const firstSectionIndex = ROASTER_ALPHABET.indexOf(roasterSections[0]?.dataset.roasterLetter);
    setActiveRoasterIndex(Math.max(0, firstSectionIndex));
  };

  const getRoasterListFadeHeight = () => {
    const fadeHeight = Number.parseFloat(
      getComputedStyle(panel).getPropertyValue('--mobile-menu-list-fade-height'),
    );

    return Number.isFinite(fadeHeight) ? fadeHeight : 16;
  };

  const updateActiveRoasterIndexFromScroll = () => {
    if (!roasterList || !roasterSections.length || roasterIndexPointerId !== null) return;

    const listBounds = roasterList.getBoundingClientRect();
    const viewport = getDocumentViewport();
    const listScrollTop = usesDocumentScroll()
      ? Math.max(0, viewport.top - listBounds.top)
      : roasterList.scrollTop;
    const reachedListEnd = usesDocumentScroll()
      ? listBounds.bottom <= viewport.bottom + 2
      : listScrollTop + roasterList.clientHeight >= roasterList.scrollHeight - 2;
    if (reachedListEnd) {
      setActiveRoasterIndex(ROASTER_ALPHABET.indexOf(roasterSections.at(-1).dataset.roasterLetter));
      return;
    }

    const scrollTarget = roasterList.scrollTop + getRoasterListFadeHeight() + 1;
    const visibleScrollTarget = usesDocumentScroll()
      ? listScrollTop + getRoasterListFadeHeight() + 1
      : scrollTarget;
    let activeSection = roasterSections[0];

    roasterSections.forEach((section) => {
      if (section.offsetTop <= visibleScrollTarget) activeSection = section;
    });

    const nextIndex = ROASTER_ALPHABET.indexOf(activeSection.dataset.roasterLetter);
    if (nextIndex !== activeRoasterIndex) setActiveRoasterIndex(nextIndex);
  };

  const scrubToPointer = (clientY) => {
    if (!roasterIndexLetters.length) return;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    roasterIndexLetters.forEach((indexLetter, index) => {
      const bounds = indexLetter.getBoundingClientRect();
      const distance = Math.abs(clientY - (bounds.top + bounds.height / 2));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== activeRoasterIndex) setActiveRoasterIndex(closestIndex, { scroll: true });
  };

  const setViewState = (nextView) => {
    const previousView = activeView;
    const showMain = nextView === 'main';

    activeView = nextView;
    targetView = nextView;
    viewStage?.setAttribute('data-active-view', nextView);
    mobileViews.forEach((view, viewName) => {
      const isActive = viewName === nextView;
      view.toggleAttribute('inert', !isActive);
      view.setAttribute('aria-hidden', String(!isActive));
      view.toggleAttribute('data-view-active', isActive && !showMain);
    });

    if (nextView === 'roasters' && previousView !== 'roasters') {
      resetRoasterListScroll();
      roasterView?.removeAttribute('data-list-scrolled');
      resetRoasterIndex();
      window.requestAnimationFrame(updateRoasterListScrollState);
    }
  };

  const resetRoasterViewState = () => {
    roasterView?.removeAttribute('data-list-scrolled');
    roasterView?.removeAttribute('data-list-scrollable');
    roasterView?.removeAttribute('data-list-at-end');
    resetRoasterIndex();
    roasterEmpty?.toggleAttribute('hidden', roasterLinks.length > 0);
  };

  const updateRoasterListScrollState = () => {
    if (!roasterList || !roasterView) return;

    const listBounds = roasterList.getBoundingClientRect();
    const viewport = getDocumentViewport();
    const listScrollTop = usesDocumentScroll()
      ? Math.max(0, viewport.top - listBounds.top)
      : roasterList.scrollTop;
    const maxScrollTop = Math.max(0, roasterList.scrollHeight - roasterList.clientHeight);
    const visibleMaxScrollTop = usesDocumentScroll()
      ? Math.max(0, listBounds.bottom - viewport.bottom + listScrollTop)
      : maxScrollTop;

    roasterView.toggleAttribute('data-list-scrolled', listScrollTop > 8);
    roasterView.toggleAttribute('data-list-scrollable', visibleMaxScrollTop > 2);
    roasterView.toggleAttribute(
      'data-list-at-end',
      visibleMaxScrollTop <= 2 || listScrollTop >= visibleMaxScrollTop - 2,
    );
    updateActiveRoasterIndexFromScroll();
  };

  const getDrillRevealItems = (view) => {
    if (!view) return [];

    const revealItems =
      view === roasterView
        ? [
            view.querySelector('.mobile-menu-roasters__heading'),
            view.querySelector('[data-roaster-index]'),
            ...view.querySelectorAll(
              '.mobile-menu-roasters__section-title, [data-roaster-name], [data-roaster-empty]:not([hidden]), .mobile-menu-roasters__directory, .mobile-menu-roasters__request',
            ),
          ]
        : [
            ...Array.from(
              view.querySelector('.mobile-menu-filter__breadcrumbs')?.children ||
                [view.querySelector('.mobile-menu-filter__heading')],
            ),
            ...Array.from(view.querySelector('.mobile-menu-filter__list')?.children || []),
          ];
    const panelBounds = usesDocumentScroll()
      ? {
          top: window.visualViewport?.offsetTop || 0,
          bottom:
            (window.visualViewport?.offsetTop || 0) +
            (window.visualViewport?.height || window.innerHeight),
        }
      : panel.getBoundingClientRect();

    return revealItems.filter((item) => {
      if (!item || item.hidden || !item.getClientRects().length) return false;

      const bounds = item.getBoundingClientRect();
      return bounds.bottom > panelBounds.top && bounds.top < panelBounds.bottom;
    });
  };

  const animateDrillContent = (view, { delay = DRILL_CONTENT_REVEAL.delay } = {}) => {
    activeDrillListAnimations.forEach((animation) => animation?.cancel());
    activeDrillListAnimations = [];

    if (reduceMotion.matches) return;

    const animations = getDrillRevealItems(view)
      .map((item, index) =>
        playTween(
          item,
          DRILL_CONTENT_REVEAL,
          (progress) => ({
            opacity: progress,
            transform: `translate3d(0, ${interpolate(DRILL_CONTENT_REVEAL.offsetY, 0, progress)}px, 0)`,
          }),
          delay + index * DRILL_CONTENT_REVEAL.stagger,
        ),
      )
      .filter(Boolean);
    activeDrillListAnimations = animations;

    Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(() => {
      if (activeDrillListAnimations !== animations) return;

      activeDrillListAnimations.forEach((animation) => animation.cancel());
      activeDrillListAnimations = [];
    });
  };

  const resetView = () => {
    viewTransitionId += 1;
    cancelViewAnimations();
    cancelDrillEntryAnimations();
    viewStage?.removeAttribute('data-view-transitioning');
    viewStage?.style.removeProperty('height');
    mobileViews.forEach((view) => {
      view.style.removeProperty('transform');
      view.style.removeProperty('opacity');
      view.querySelectorAll('[data-roaster-reveal], [data-menu-view-reveal]').forEach((group) => {
        group.style.removeProperty('opacity');
        group.style.removeProperty('transform');
      });
    });
    setViewState('main');
    resetRoasterViewState();
  };

  const getViewTrigger = (viewName) =>
    viewOpenButtons.find((button) => button.dataset.menuViewTarget === viewName);

  const getViewParent = (view) =>
    (view?.querySelector('[data-menu-view-parent]') || view?.querySelector('[data-menu-view-back]'))
      ?.dataset.menuViewBackTarget || 'main';

  const moveViewFocus = (nextView, focusTarget = null) => {
    requestAnimationFrame(() => {
      const target = focusTarget || mobileViews.get(nextView)?.querySelector('[data-menu-view-back]');
      target?.focus({ preventScroll: true });
    });
  };

  const animateView = async (nextView, { instant = false, moveFocus = false, focusTarget = null } = {}) => {
    const outgoing = mobileViews.get(activeView);
    const incoming = mobileViews.get(nextView);
    if (!viewStage || !outgoing || !incoming || nextView === activeView) return;

    const currentTransition = ++viewTransitionId;
    const involvesBrandView = BRAND_VIEW_NAMES.has(activeView) || BRAND_VIEW_NAMES.has(nextView);
    const viewTiming = involvesBrandView ? BRAND_DRILL_VIEW : DRILL_VIEW;
    const viewEnterDuration = viewTiming.enterDuration;
    const viewExitDuration = viewTiming.exitDuration;
    const viewEasing = viewTiming.easing;
    const activeViewParent = getViewParent(outgoing);
    const isBackNavigation = activeView !== 'main' && activeViewParent === nextView;
    const outgoingOffset = isBackNavigation ? viewTiming.distance : -viewTiming.distance;
    const incomingOffset = isBackNavigation ? -viewTiming.distance : viewTiming.distance;

    cancelViewAnimations();
    if (nextView !== 'main') cancelDrillEntryAnimations();
    targetView = nextView;
    resetPanelScroll();

    if (instant || reduceMotion.matches || !incoming.animate) {
      cancelDrillEntryAnimations();
      setViewState(nextView);
      if (nextView === 'main') resetRoasterViewState();
      if (moveFocus) moveViewFocus(nextView, focusTarget);
      return;
    }

    outgoing.toggleAttribute('inert', true);
    incoming.toggleAttribute('inert', true);
    outgoing.setAttribute('aria-hidden', 'true');
    incoming.setAttribute('aria-hidden', 'true');
    incoming.setAttribute('data-view-active', 'true');
    viewStage.dataset.viewTransitioning = 'true';
    if (activeView === 'main' || nextView === 'main') {
      viewStage.style.height = `${Math.max(outgoing.scrollHeight, incoming.scrollHeight)}px`;
    } else {
      viewStage.style.removeProperty('height');
    }

    if (nextView === 'roasters') {
      resetRoasterListScroll();
      roasterView?.removeAttribute('data-list-scrolled');
      resetRoasterIndex();
    }

    activeViewAnimations = [
      outgoing.animate(
        [
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
          { opacity: 0, transform: `translate3d(${outgoingOffset}px, 0, 0)` },
        ],
        { duration: viewExitDuration, easing: viewEasing, fill: 'both' },
      ),
      incoming.animate(
        [
          { opacity: 0, transform: `translate3d(${incomingOffset}px, 0, 0)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        ],
        { duration: viewEnterDuration, easing: viewEasing, fill: 'both' },
      ),
    ].filter(Boolean);
    if (nextView !== 'main') animateDrillContent(incoming);

    await Promise.all(activeViewAnimations.map((animation) => animation.finished.catch(() => {})));

    if (currentTransition !== viewTransitionId) return;

    cancelViewAnimations();
    outgoing.style.removeProperty('transform');
    incoming.style.removeProperty('transform');
    viewStage.removeAttribute('data-view-transitioning');
    viewStage.style.removeProperty('height');
    setViewState(nextView);
    if (nextView === 'main') cancelDrillEntryAnimations();
    if (nextView === 'main') resetRoasterViewState();

    if (moveFocus) moveViewFocus(nextView, focusTarget);
  };

  const finishInstantly = (opening, { restoreFocus = true } = {}) => {
    cancelAnimations();
    menu.dataset.menuInstant = 'true';
    menu.open = opening;
    panel.toggleAttribute('inert', !opening);
    setMenuBackgroundInert(opening);
    targetOpen = opening;
    delete menu.dataset.menuAnimating;
    delete menu.dataset.menuInitial;
    requestAnimationFrame(() => delete menu.dataset.menuInstant);

    if (!opening) {
      disclosureItems.forEach((item) => finishDisclosureInstantly(item, false));
      menu.querySelectorAll('.mobile-menu__item[open]').forEach((item) => item.removeAttribute('open'));
      resetView();
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }
  };

  const animateMenu = async (opening, { instant = false, restoreFocus = true } = {}) => {
    targetOpen = opening;
    const currentTransition = ++transitionId;

    if (instant || reduceMotion.matches || !panel?.animate) {
      finishInstantly(opening, { restoreFocus });
      return;
    }

    menu.dataset.menuAnimating = opening ? 'open' : 'close';
    panel.toggleAttribute('inert', !opening);
    if (opening) setMenuBackgroundInert(true);
    const topStart = readTransform(topLine);
    const bottomStart = readTransform(bottomLine);

    if (opening && !menu.open) {
      menu.dataset.menuInitial = 'true';
      menu.open = true;
      await new Promise(requestAnimationFrame);

      if (currentTransition !== transitionId) return;
    }

    const panelStart = {
      opacity: readOpacity(panel),
      y: readTransform(panel).y,
    };
    const visibleRevealGroups =
      activeView === 'main'
        ? menuRevealLines
        : Array.from(mobileViews.get(activeView)?.querySelectorAll('[data-roaster-reveal], [data-menu-view-reveal]') || []);
    const groupStarts = visibleRevealGroups.map((group) => {
      const transform = readTransform(group);
      return {
        opacity: readOpacity(group),
        y: transform.y,
      };
    });
    cancelAnimations();
    if (!opening) cancelDrillEntryAnimations();

    const panelTarget = opening ? 1 : 0;
    const panelYTarget = opening ? 0 : MENU_PANEL_CLOSED.y;
    const contentTarget = opening ? MENU_CONTENT_OPEN : MENU_CONTENT_CLOSED;
    const panelTiming = opening ? MENU_PANEL_ENTER : MENU_PANEL_EXIT;
    const lineTiming = opening ? MENU_LINE_ENTER : MENU_LINE_EXIT;
    const topTarget = opening ? { rotation: 45, y: 4 } : { rotation: 0, y: 0 };
    const bottomTarget = opening ? { rotation: -45, y: -4 } : { rotation: 0, y: 0 };

    activeAnimations = [
      playTween(
        panel,
        panelTiming,
        (progress) => ({
          opacity: clamp(interpolate(panelStart.opacity, panelTarget, progress)),
          transform: `translate3d(0, ${interpolate(panelStart.y, panelYTarget, progress)}px, 0)`,
        }),
      ),
      ...visibleRevealGroups.map((group, index) => {
        const start = groupStarts[index];
        const delay = opening && activeView === 'main' ? MENU_LINE_START + Math.min(index, MENU_LINE_MAX_STEPS) * MENU_LINE_DELAY : 0;

        return playTween(
          group,
          lineTiming,
          (progress) => ({
            opacity: clamp(interpolate(start.opacity, contentTarget.opacity, progress)),
            transform: `translateY(${interpolate(start.y, contentTarget.y, progress)}px)`,
          }),
          delay,
        );
      }),
      playTween(topLine, ICON_MORPH, (progress) => ({
        transform: `translateY(${interpolate(topStart.y, topTarget.y, progress)}px) rotate(${interpolate(
          topStart.rotation,
          topTarget.rotation,
          progress,
        )}deg)`,
      })),
      playTween(bottomLine, ICON_MORPH, (progress) => ({
        transform: `translateY(${interpolate(bottomStart.y, bottomTarget.y, progress)}px) rotate(${interpolate(
          bottomStart.rotation,
          bottomTarget.rotation,
          progress,
        )}deg)`,
      })),
    ].filter(Boolean);

    delete menu.dataset.menuInitial;

    await Promise.all(activeAnimations.map((animation) => animation.finished.catch(() => {})));

    if (currentTransition !== transitionId) return;

    if (!opening) {
      menu.open = false;
      setMenuBackgroundInert(false);
      disclosureItems.forEach((item) => finishDisclosureInstantly(item, false));
      menu.querySelectorAll('.mobile-menu__item[open]').forEach((item) => item.removeAttribute('open'));
      resetView();
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }

    cancelAnimations();
    delete menu.dataset.menuAnimating;
  };

  trigger?.addEventListener('click', (event) => {
    if (!mobileViewport.matches) return;

    event.preventDefault();
    const opening = !targetOpen;

    if (event.detail === 0) {
      return;
    }

    animateMenu(opening);
  });

  trigger?.addEventListener('pointerdown', () => {
    trigger.dataset.pointerFocus = 'true';
  });

  trigger?.addEventListener('keydown', (event) => {
    delete trigger.dataset.pointerFocus;

    if (mobileViewport.matches && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      animateMenu(!targetOpen, { instant: true });
    }
  });

  trigger?.addEventListener('blur', () => {
    delete trigger.dataset.pointerFocus;
  });

  disclosureSummaries.forEach((summary) => {
    summary.addEventListener('click', (event) => {
      if (event.detail !== 0) return;

      const item = summary.closest('.mobile-menu__item');
      item.dataset.iconInstant = 'true';
      requestAnimationFrame(() => delete item.dataset.iconInstant);
    });
  });

  disclosureSummaries.forEach((summary) => {
    summary.addEventListener('click', (event) => {
      if (!mobileViewport.matches) return;

      event.preventDefault();
      const item = summary.closest('.mobile-menu__item');
      const state = disclosureStates.get(item);
      animateDisclosure(item, !state.targetOpen, { instant: event.detail === 0 });
    });
  });

  panel?.addEventListener('click', (event) => {
    if (isSameTabNavigationClick(event, panel)) {
      animateMenu(false, { instant: true, restoreFocus: false });
      return;
    }

    const sourceView = event.target.closest('[data-mobile-menu-view]')?.dataset.mobileMenuView;
    if (!mobileViewport.matches || activeView !== 'main' || (sourceView && sourceView !== 'main')) return;

    disclosureItems.forEach((item) => {
      const state = disclosureStates.get(item);
      if (state?.targetOpen && !item.contains(event.target)) animateDisclosure(item, false);
    });
  });

  viewOpenButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      const keyboardActivation = event.detail === 0;
      if (!keyboardActivation) button.blur();
      animateView(button.dataset.menuViewTarget, {
        instant: keyboardActivation,
        moveFocus: keyboardActivation,
      });
    });
  });

  viewBackButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      const keyboardActivation = event.detail === 0;
      if (!keyboardActivation) button.blur();
      const currentView = button.closest('[data-mobile-menu-view]')?.dataset.mobileMenuView;
      const parentView = button.dataset.menuViewBackTarget || 'main';
      const focusView = button.dataset.menuViewFocusTarget || currentView;
      animateView(parentView, {
        instant: keyboardActivation,
        moveFocus: keyboardActivation,
        focusTarget: getViewTrigger(focusView),
      });
    });
  });

  roasterList?.addEventListener('scroll', updateRoasterListScrollState, { passive: true });
  window.addEventListener('scroll', () => {
    if (usesDocumentScroll() && targetView === 'roasters') updateRoasterListScrollState();
  }, { passive: true });
  window.addEventListener('resize', updateRoasterListScrollState, { passive: true });

  roasterIndex?.addEventListener('pointerdown', (event) => {
    if (roasterIndexPointerId !== null || !event.isPrimary) return;

    event.preventDefault();
    roasterIndexPointerId = event.pointerId;
    roasterIndex.setPointerCapture(event.pointerId);
    roasterIndex.setAttribute('data-scrubbing', '');
    scrubToPointer(event.clientY);
  });

  roasterIndex?.addEventListener('pointermove', (event) => {
    if (event.pointerId !== roasterIndexPointerId) return;

    event.preventDefault();
    scrubToPointer(event.clientY);
  });

  const finishRoasterScrub = (event) => {
    if (event.pointerId !== roasterIndexPointerId) return;

    roasterIndexPointerId = null;
    roasterIndex?.removeAttribute('data-scrubbing');
    updateActiveRoasterIndexFromScroll();
  };

  roasterIndex?.addEventListener('pointerup', finishRoasterScrub);
  roasterIndex?.addEventListener('pointercancel', finishRoasterScrub);
  roasterIndex?.addEventListener('lostpointercapture', finishRoasterScrub);

  roasterIndex?.addEventListener('keydown', (event) => {
    let nextIndex = activeRoasterIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex += 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex -= 1;
        break;
      case 'PageDown':
        nextIndex += 5;
        break;
      case 'PageUp':
        nextIndex -= 5;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = ROASTER_ALPHABET.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveRoasterIndex(nextIndex, { scroll: true });
  });

  mobileViewport.addEventListener('change', () => {
    transitionId += 1;
    cancelAnimations();
    cancelViewAnimations();
    cancelDrillEntryAnimations();
    disclosureItems.forEach((item) => finishDisclosureInstantly(item, false));
    menu.querySelectorAll('.mobile-menu__item[open]').forEach((item) => item.removeAttribute('open'));
    menu.open = false;
    targetOpen = false;
    panel.toggleAttribute('inert', true);
    setMenuBackgroundInert(false);
    resetView();
    delete menu.dataset.menuAnimating;
    delete menu.dataset.menuInitial;
    delete menu.dataset.menuInstant;
  });

  reduceMotion.addEventListener('change', () => {
    if (!reduceMotion.matches) return;

    transitionId += 1;
    viewTransitionId += 1;
    finishInstantly(targetOpen, { restoreFocus: false });
    disclosureItems.forEach((item) => {
      const state = disclosureStates.get(item);
      finishDisclosureInstantly(item, Boolean(targetOpen && state?.targetOpen));
    });
    cancelViewAnimations();
    cancelDrillEntryAnimations();
    viewStage?.removeAttribute('data-view-transitioning');
    viewStage?.style.removeProperty('height');
    mobileViews.forEach((view) => {
      view.style.removeProperty('transform');
      view.style.removeProperty('opacity');
    });
    setViewState(targetOpen ? targetView : 'main');
    if (!targetOpen || targetView === 'main') resetRoasterViewState();
  });

  loadRoasterVendors();

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && targetOpen) {
      event.preventDefault();
      if (activeView !== 'main') {
        const currentView = mobileViews.get(activeView);
        const parentView = getViewParent(currentView);
        animateView(parentView, {
          instant: true,
          moveFocus: true,
          focusTarget: getViewTrigger(activeView),
        });
      } else {
        animateMenu(false, { instant: true });
      }
    }
  });
});
